/**
 * MS cart helpers (loaded on every page, ~4 KB).
 *
 * MS.cart.add(items, opts)  - adds several lines in one request using Concept's own
 *                             cart flow, so the cart drawer, cart count and cart page refresh.
 * MS.money(cents)           - formats money with the shop's money format.
 * Gift sync                 - keeps "linked" MS gifts in the cart in step with the
 *                             qualifying product quantity (adds, removes or resizes gift lines).
 *
 * Gift rules travel with the cart lines themselves as hidden properties:
 *   main product lines: _ms_gift_rules = {"p":<product id>,"mode":"cumulative|highest","g":[{"m":2,"v":<variant id>,"q":1}]}
 *   gift lines:         _ms_gift       = "<product id>:<variant id>"
 * Properties starting with "_" are hidden in Concept's cart and at checkout.
 */
(function () {
  'use strict';

  const MS = (window.MS = window.MS || {});
  const SOURCE_SYNC = 'ms-gift-sync';

  const routes = () => (window.theme && window.theme.routes) || {};
  const rootUrl = () => routes().root_url || '/';
  const join = (base, path) => (base.endsWith('/') ? base.slice(0, -1) : base) + path;
  const cartUrl = () => routes().cart_url || join(rootUrl(), '/cart');
  const addUrl = () => routes().cart_add_url || join(rootUrl(), '/cart/add');
  const updateUrl = () => routes().cart_update_url || join(rootUrl(), '/cart/update');
  const withJs = (url) => (url.endsWith('.js') ? url : url + '.js');

  /* ---------------- money ---------------- */
  MS.money = function (cents, format) {
    const fmt = format || MS.moneyFormat || (window.Shopify && Shopify.money_format) || '${{amount}}';
    const value = Number(cents) || 0;
    const formatWith = (precision, thousands = ',', decimal = '.') => {
      const fixed = (value / 100).toFixed(precision);
      const [whole, frac] = fixed.split('.');
      const withSep = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? withSep + decimal + frac : withSep;
    };
    return String(fmt)
      .replace(/<[^>]*>/g, '')
      .replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => {
        switch (key) {
          case 'amount': return formatWith(2);
          case 'amount_no_decimals': return formatWith(0);
          case 'amount_with_comma_separator': return formatWith(2, '.', ',');
          case 'amount_no_decimals_with_comma_separator': return formatWith(0, '.');
          case 'amount_with_apostrophe_separator': return formatWith(2, "'");
          case 'amount_with_space_separator': return formatWith(2, ' ', ',');
          default: return formatWith(2);
        }
      });
  };

  /* ---------------- cart requests ---------------- */
  function bundledSections() {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } })
    );
    return sections;
  }

  async function getCart() {
    const res = await fetch(withJs(cartUrl()), { headers: { Accept: 'application/json' } });
    return res.json();
  }

  function publishCart(cart, source, extra = {}) {
    if (window.theme && theme.pubsub && theme.pubsub.PUB_SUB_EVENTS) {
      theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, Object.assign({ source, cart }, extra));
    } else {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
    }
  }

  /**
   * Add several lines to the cart.
   * @param {Array<{id:number|string, quantity:number, properties?:object, selling_plan?:number|string}>} items
   * @param {{source?:string, opener?:Element, openDrawer?:boolean}} opts
   * @returns {Promise<{ok:boolean, cart?:object, error?:string}>}
   */
  MS.cart = MS.cart || {};
  MS.cart.add = async function (items, opts = {}) {
    const source = opts.source || 'ms';
    const clean = items
      .filter((i) => i && i.id && Number(i.quantity) > 0)
      .map((i) => {
        const line = { id: Number(i.id), quantity: Number(i.quantity) };
        if (i.properties && Object.keys(i.properties).length) line.properties = i.properties;
        if (i.selling_plan) line.selling_plan = Number(i.selling_plan);
        return line;
      });
    if (!clean.length) return { ok: false, error: 'Nothing to add' };

    const sections = bundledSections();
    const payload = { items: clean };
    if (sections.length) {
      payload.sections = sections.join(',');
      payload.sections_url = window.location.pathname;
    }

    let parsed;
    try {
      const res = await fetch(withJs(addUrl()), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(payload),
      });
      parsed = await res.json();
    } catch (error) {
      console.error('[MS] add to cart failed', error);
      return { ok: false, error: (window.theme && theme.strings && theme.strings.cartError) || 'Could not reach the cart. Please try again.' };
    }

    if (parsed && parsed.status) {
      const cart = await getCart().catch(() => null);
      if (cart) publishCart(cart, source, { errors: parsed.description });
      return { ok: false, error: parsed.description || parsed.message || 'Something went wrong', cart };
    }

    const cart = await getCart();
    if (parsed && parsed.sections) cart.sections = parsed.sections;
    publishCart(cart, source);
    document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: parsed } }));

    const cartType = window.theme && theme.settings && theme.settings.cartType;
    const drawer = document.querySelector('cart-drawer');
    if (opts.openDrawer !== false) {
      if (cartType === 'page' || !drawer || document.body.classList.contains('template-cart')) {
        window.location.href = cartUrl();
      } else if (typeof drawer.show === 'function') {
        drawer.show(opts.opener || null);
      }
    }
    return { ok: true, cart };
  };

  /* ---------------- gift sync ---------------- */
  const GIFT_PROP = '_ms_gift';
  const RULE_PROP = '_ms_gift_rules';
  let syncing = false;
  let pending = null;

  function parseRules(value) {
    try {
      const rules = typeof value === 'string' ? JSON.parse(value) : value;
      return rules && rules.p && Array.isArray(rules.g) ? rules : null;
    } catch (e) {
      return null;
    }
  }

  /** Works out which gift lines each product should have, given the cart. */
  function desiredGifts(cart) {
    const byProduct = new Map();
    for (const item of cart.items || []) {
      const props = item.properties || {};
      if (props[GIFT_PROP]) continue;
      const rules = parseRules(props[RULE_PROP]);
      const pid = String(item.product_id);
      const entry = byProduct.get(pid) || { qty: 0, rules: null };
      entry.qty += item.quantity;
      if (rules && String(rules.p) === pid) entry.rules = rules;
      byProduct.set(pid, entry);
    }

    const desired = new Map(); // key "pid:variant" -> qty
    for (const [pid, { qty, rules }] of byProduct) {
      if (!rules) continue;
      let eligible = rules.g.filter((g) => qty >= Number(g.m || 1));
      if (rules.mode === 'highest' && eligible.length) {
        const top = Math.max(...eligible.map((g) => Number(g.m || 1)));
        eligible = eligible.filter((g) => Number(g.m || 1) === top);
      }
      for (const g of eligible) {
        if (!g.v) continue;
        const key = `${pid}:${g.v}`;
        desired.set(key, (desired.get(key) || 0) + Math.max(1, Number(g.q) || 1));
      }
    }
    return { desired, managedProducts: new Set([...byProduct].filter(([, e]) => e.rules).map(([p]) => p)) };
  }

  async function syncGifts(cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    const { desired } = desiredGifts(cart);

    const updates = {};
    const current = new Map();
    for (const item of cart.items) {
      const tag = item.properties && item.properties[GIFT_PROP];
      if (!tag) continue;
      const key = String(tag);
      current.set(key, (current.get(key) || 0) + item.quantity);
      const want = desired.get(key) || 0;
      if (want === 0) updates[item.key] = 0;
    }
    // Resize gift lines that exist but have the wrong quantity (first line holds the quantity)
    const seen = new Set();
    for (const item of cart.items) {
      const tag = item.properties && item.properties[GIFT_PROP];
      if (!tag || updates[item.key] === 0) continue;
      const key = String(tag);
      const want = desired.get(key) || 0;
      if (!seen.has(key)) {
        seen.add(key);
        if (item.quantity !== want) updates[item.key] = want;
      } else {
        updates[item.key] = 0; // duplicate gift line
      }
    }
    const adds = [];
    for (const [key, qty] of desired) {
      if (current.has(key)) continue;
      const variantId = key.split(':')[1];
      adds.push({ id: Number(variantId), quantity: qty, properties: { [GIFT_PROP]: key } });
    }

    if (!Object.keys(updates).length && !adds.length) return;

    syncing = true;
    try {
      // Ask the last request to re-render the cart sections Concept has on the page
      const sections = bundledSections();
      const withSections = (body) =>
        sections.length ? Object.assign(body, { sections: sections.join(','), sections_url: window.location.pathname }) : body;
      const post = (url, body) =>
        fetch(withJs(url), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        }).then((r) => r.json());

      let last = null;
      if (Object.keys(updates).length) {
        const body = { updates };
        last = await post(updateUrl(), adds.length ? body : withSections(body));
      }
      if (adds.length) {
        last = await post(addUrl(), withSections({ items: adds }));
      }
      const fresh = await getCart();
      if (last && last.sections) fresh.sections = last.sections;
      publishCart(fresh, SOURCE_SYNC);
      if (!(last && last.sections)) document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
    } catch (error) {
      console.warn('[MS] gift sync skipped', error);
    } finally {
      syncing = false;
    }
  }

  function queueSync(cart) {
    if (syncing) return;
    clearTimeout(pending);
    pending = setTimeout(() => syncGifts(cart), 150);
  }

  MS.cart.syncGifts = () => getCart().then(syncGifts);

  function subscribe() {
    if (!(window.theme && theme.pubsub && theme.pubsub.PUB_SUB_EVENTS)) return false;
    theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (!event || event.source === SOURCE_SYNC) return;
      const cart = event.cart;
      if (!cart || !Array.isArray(cart.items)) {
        getCart().then(queueSync).catch(() => {});
        return;
      }
      const hasRules = cart.items.some((i) => i.properties && (i.properties[RULE_PROP] || i.properties[GIFT_PROP]));
      if (hasRules) queueSync(cart);
    });
    return true;
  }

  /* ---------------- shared script loader (Swiper, LazyLoad) ---------------- */
  const loaded = new Map();
  /** Loads a script once and resolves when it is ready. Used by MS sections for shared libraries. */
  MS.loadScript = function (src) {
    if (!src) return Promise.resolve();
    if (loaded.has(src)) return loaded.get(src);
    const promise = new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[data-ms-src="${src}"]`);
      const script = existing || document.createElement('script');
      script.addEventListener('load', () => resolve(), { once: true });
      script.addEventListener('error', () => reject(new Error(`Could not load ${src}`)), { once: true });
      if (!existing) {
        script.src = src;
        script.async = true;
        script.dataset.msSrc = src;
        document.head.appendChild(script);
      }
    });
    loaded.set(src, promise);
    return promise;
  };

  /* ---------------- smooth scrolling for same-page links ----------------
     Any link to a section on the same page (#section-id), plus MS review badges,
     glides there instead of jumping, and stops just below the sticky header. */
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /** Space to leave for the sticky header at the destination. */
  function headerOffset(goingDown) {
    const header = document.querySelector('header[data-sticky-type]');
    const type = header ? header.getAttribute('data-sticky-type') : 'none';
    if (!header || type === 'none') return 12;
    // "Show on scroll up" headers hide while scrolling down, so only leave room when going up
    if (type === 'on-scroll-up' && goingDown) return 12;
    const sticky = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sticky-header-height')) || header.getBoundingClientRect().height;
    return sticky + 12;
  }

  MS.scrollToElement = function (el) {
    if (!el) return;
    const rectTop = el.getBoundingClientRect().top;
    const top = rectTop + window.scrollY - headerOffset(rectTop > 0);
    window.scrollTo({ top: Math.max(0, top), behavior: reduceMotion() ? 'auto' : 'smooth' });
    // Move keyboard focus to the section without a second jump
    if (!el.hasAttribute('tabindex') && !/^(A|BUTTON|INPUT|SELECT|TEXTAREA)$/.test(el.tagName)) el.setAttribute('tabindex', '-1');
    try { el.focus({ preventScroll: true }); } catch (e) { /* older browsers */ }
  };

  function findTarget(hash) {
    if (!hash || hash === '#') return null;
    let id = hash.slice(1);
    try { id = decodeURIComponent(id); } catch (e) { /* keep raw */ }
    return document.getElementById(id);
  }

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!event.target.closest) return;

    // MS review badges: data-ms-scroll="#looxReviews" (also accepts a class selector)
    const badge = event.target.closest('[data-ms-scroll]');
    if (badge) {
      const target = badge.getAttribute('data-ms-scroll');
      if (!target || target.charAt(0) === '/' || target.indexOf('http') === 0) return;
      let el = null;
      try { el = document.querySelector(target); } catch (e) { el = null; }
      if (!el && target.charAt(0) === '#') el = findTarget(target);
      if (!el) return;
      event.preventDefault();
      MS.scrollToElement(el);
      return;
    }

    const link = event.target.closest('a[href*="#"]');
    if (!link || (link.target && link.target !== '_self')) return;
    // Bundle buttons are handled by the MS Bundle Selector (it selects the tier, then scrolls)
    if (link.hasAttribute('data-ms-bundle-pick') && document.querySelector('ms-bundle')) return;
    // Leave tabs, accordions and drawer toggles to their own scripts
    if (link.getAttribute('role') === 'tab' || link.hasAttribute('aria-controls') || link.hasAttribute('data-no-smooth-scroll')) return;

    let url;
    try { url = new URL(link.getAttribute('href'), window.location.href); } catch (e) { return; }
    if (url.origin !== window.location.origin || url.pathname !== window.location.pathname || url.search !== window.location.search) return;
    const el = findTarget(url.hash);
    if (!el) return;

    event.preventDefault();
    MS.scrollToElement(el);
    if (window.history && history.pushState && url.hash !== window.location.hash) history.pushState(null, '', url.hash);
  });

  if (window.MS_GIFT_SYNC !== false) {
    if (!subscribe()) {
      document.addEventListener('DOMContentLoaded', () => subscribe());
    }
  }
})();
